import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { cn } from '@repo/ui/src/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import { markChipHintLearned, useChipAffordance } from '../lib/chipAffordance';
import { quotaExplanation } from '../lib/attendanceQuota';

/**
 * The excused-absence quota, as a chip that explains itself (spec P2.8 §1).
 *
 * The chip is a Popover TRIGGER, which is why it lives here and not inside the
 * "Có phép" button it used to sit in: that button's tap marks attendance, and
 * an interactive descendant would either swallow the mark or have to be a
 * non-focusable span with a stopPropagation guard. Out here it is a real
 * `<button>` (Radix renders one — no `asChild`, so the `Badge` span nests
 * inside it legally) that is reachable by keyboard and announces itself.
 *
 * Renders NOTHING when `quotaExplanation` returns null — which is exactly when
 * `excusedQuotaDisplay` has no badge to show, so the chip and the sentence
 * behind it can never exist independently.
 */
export function QuotaExplanationPopover({
  studentName,
  quota,
  participant,
  limit,
  overLimit,
}: {
  studentName: string;
  /** The composed fragment the chip shows ("2/3", "2/3 (đã bù 1)"). */
  quota: string;
  participant: { excusedThisMonth: number; madeUpThisMonth: number; chargeAbsent: boolean; pricingType: string };
  limit: number | null;
  overLimit: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation('scheduling');
  const [open, setOpen] = useState(false);
  const affordance = useChipAffordance();
  const explanation = quotaExplanation(participant, limit);
  if (!explanation) return null;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markChipHintLearned(); }}>
      {/* 44px tap target pinned in px — --spacing is 0.2rem app-wide, so a
          spacing-derived min-height lands around 35px. */}
      <PopoverTrigger
        aria-label={t('attendance.quotaChipAria', { name: studentName })}
        className="inline-flex min-h-[44px] shrink-0 items-center"
      >
        <Badge variant={overLimit ? 'danger' : 'warning'} className={cn('font-medium', affordance)}>
          {t('attendance.quotaChip', { quota })}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-sm" collisionPadding={12}>
        {t(`attendance.quotaPopover.${explanation.key}`, {
          name: studentName,
          used: explanation.used,
          limit: explanation.limit,
          madeUp: explanation.madeUp,
        })}
        {/* The refund rule is worth stating before it has happened: a parent
            asking "what if she makes it up?" gets the answer from the chip. */}
        {explanation.madeUp == null && (
          <p className="mt-1.5 text-surface-500">{t('attendance.quotaPopover.madeUpRule')}</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
